import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from '../firebase/useFirestore'
import { resolveIndicatorFieldType } from '../helpers/visualisationDefaults'
import useIndicatorMomentCount from './useIndicatorMomentCount'

// Resolves the display-time options-bar inputs for a single indicator card:
//   - resolvedFieldType: e.g. 'multiple-moment' / 'scale-development'
//   - subItems:          options shown in the palette color picker
//
// Keeps IndicatorCardContent and parent-effect cards in sync so the options
// bar behaves identically regardless of where the indicator is rendered.
// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// When `templateData` is provided, moment-count + sub-item lookups are
// derived from the template blob instead of subscribing to the current
// tenant's Firestore. Live callers (dashboard) pass nothing and keep
// their previous behaviour.
const useIndicatorCardControls = (indicator, templateData = null) => {
    const isMultiple = indicator?.Type === 'multiple-one' || indicator?.Type === 'multiple-multiple'
    const isParagraph = indicator?.Type === 'paragraph'
    const isMatrix = indicator?.Type === 'matrix-one' || indicator?.Type === 'matrix-multiple'
    // MSI matrix questions collapse all rows into a single synthetic 'Gemiddelde'
    // series in useMatrixData — so the color picker should mirror that and show
    // one swatch (not N row swatches that won't render on the chart).
    const isMsiMatrix = isMatrix && indicator?.Standard === 'true'

    const isTemplate = !!templateData

    const multipleOptions = useFirestoreGeneral('MultipleQuestionOptions', 'Field', !isTemplate && isMultiple ? indicator.ID : '')
    const analysisCategories = useFirestoreGeneral('AnalysisCategories', 'FieldID', !isTemplate && isParagraph ? indicator.ID : '')
    const matrixRows = useFirestoreGeneralOrderBy('MatrixQuestionRows', 'Field', !isTemplate && isMatrix && !isMsiMatrix ? indicator.ID : '', 'Position', 'asc')

    const momentCount = useIndicatorMomentCount(indicator?.QuestionnaireID || '', templateData)
    const resolvedFieldType = resolveIndicatorFieldType(indicator?.Type, momentCount)

    let subItems = []
    if (isMultiple && multipleOptions) {
        // The "Other" option is rendered by MultipleData under the synthetic
        // id '__other__' (see MultipleData.jsx). Match that key here so the
        // picker swatch and the chart bar resolve to the same color override.
        subItems = multipleOptions.map(o => o.Option === 'other'
            ? { ID: '__other__', label: 'Anders, namelijk..', docid: o.docid }
            : { ID: o.ID, label: o.Option, docid: o.docid }
        )
    } else if (isParagraph && analysisCategories) {
        subItems = analysisCategories.map(c => ({ ID: c.ID, label: c.Categorie, docid: c.docid }))
    } else if (isMsiMatrix) {
        subItems = [{ ID: 'gemiddelde', label: 'Gemiddelde' }]
    } else if (isMatrix && Array.isArray(matrixRows)) {
        subItems = matrixRows.map(r => ({ ID: r.ID, label: r.Title, docid: r.docid }))
    }

    return { resolvedFieldType, subItems }
}

export default useIndicatorCardControls
